import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Row,
  Spin,
  message,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import HeaderCreateNewsComponent from '../../components/header/HeaderCreateNewsComponent';
import DragUploadComponent from '../../components/upload/DragUploadComponent';
import OSSUploadWrapper from '../../components/upload/OSSUploadWrapper';
import InputComponent from '../../components/input/InputComponent';
import DateComponent from '../../components/date/DateComponent';
import MessageConfirmModal from '../../components/messageModal/MessageConfirmModal';

import { EVENTS_URL } from '../../api/URLs';
import { getDataRequest, postDataRequest, putDataRequest } from '../../api/serviceMethods';

import { DEFAULT_STANDARD_LAYOUT } from './components/DynamicFormBuilder';
import './components/event-builder.css';

// Helper to strip non-schema properties (like isFixed) from layout items before sending to API
const sanitizeLayoutItem = (item) => {
  if (!item) return item;
  const sanitized = {
    _id: item._id,
    questionType: item.questionType,
    title: item.title,
    placeholder: item.placeholder || '',
    isRequired: Boolean(item.isRequired),
    answers: Array.isArray(item.answers) ? item.answers : [],
  };
  if (item.questionType === 'phone') {
    sanitized.defaultCountryCode = item.defaultCountryCode || '+86';
    if (item.countryCodes && Array.isArray(item.countryCodes)) {
      sanitized.countryCodes = item.countryCodes.map((cc) => ({
        label: cc.label,
        value: cc.value,
        _id: cc._id,
      }));
    }
  }
  if (item.code) {
    sanitized.code = item.code;
  }
  return sanitized;
};

const sanitizeLayout = (layout) => {
  if (!Array.isArray(layout)) return [];
  return layout.map(sanitizeLayoutItem);
};

function EventUpload() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [eventCode, setEventCode] = useState(id || '');
  const messageModalRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  // Active language for multilingual content ('cn')
  const activeLang = 'cn';

  // Poster image state
  const [picUrl, setPicUrl] = useState('');
  const [posterFileList, setPosterFileList] = useState([]);

  // Status state: 'active' | 'inactive' (defaults to 'active')
  const [status, setStatus] = useState('active');

  // Date fields
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Localized data keyed by 'cn', 'en', 'zh-HK'
  const [localizeData, setLocalizeData] = useState({
    cn: {
      title: '',
      subtitle: '',
      layout: sanitizeLayout(DEFAULT_STANDARD_LAYOUT),
    },
    en: {
      title: '',
      subtitle: '',
      layout: sanitizeLayout(DEFAULT_STANDARD_LAYOUT),
    },
    'zh-HK': {
      title: '',
      subtitle: '',
      layout: sanitizeLayout(DEFAULT_STANDARD_LAYOUT),
    },
  });

  // Load existing event if editing
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getDataRequest(`${EVENTS_URL}/${id}`)
      .then((res) => {
        setLoading(false);
        const existing = res?.data || res;
        if (!existing) return;

        if (existing.eventId) {
          setEventCode(existing.eventId);
        }

        // Parse dates
        if (existing.startDate) {
          setStartDate(dayjs(existing.startDate).isValid() ? dayjs(existing.startDate).format('YYYY-MM-DD') : (existing.startDate || ''));
        }
        if (existing.endDate) {
          setEndDate(dayjs(existing.endDate).isValid() ? dayjs(existing.endDate).format('YYYY-MM-DD') : (existing.endDate || ''));
        }

        // Status
        setStatus(existing.status === 'inactive' ? 'inactive' : 'active');

        // Poster
        const imgObj = existing.images && existing.images.length > 0 ? existing.images[0] : null;
        const posterUrl = imgObj?.url || existing.poster || '';
        if (posterUrl) {
          setPicUrl(posterUrl);
          setPosterFileList([
            {
              uid: '1',
              name: imgObj?.name || 'poster.jpg',
              status: 'done',
              url: posterUrl,
              size: imgObj?.size || 1024 * 500,
            },
          ]);
        }

        // Localized info
        const existingLocalize = existing.localize || {};
        const fallbackTitle = existing.title || '';
        const fallbackLayout = existing.layout && existing.layout.length > 0
          ? existing.layout
          : DEFAULT_STANDARD_LAYOUT;

        setLocalizeData({
          cn: {
            title: existingLocalize.cn?.title || fallbackTitle,
            subtitle: existingLocalize.cn?.subtitle || '',
            layout: Array.isArray(existingLocalize.cn?.layout) && existingLocalize.cn.layout.length > 0
              ? existingLocalize.cn.layout
              : JSON.parse(JSON.stringify(fallbackLayout)),
          },
          en: {
            title: existingLocalize.en?.title || '',
            subtitle: existingLocalize.en?.subtitle || '',
            layout: Array.isArray(existingLocalize.en?.layout) && existingLocalize.en.layout.length > 0
              ? existingLocalize.en.layout
              : JSON.parse(JSON.stringify(fallbackLayout)),
          },
          'zh-HK': {
            title: existingLocalize['zh-HK']?.title || '',
            subtitle: existingLocalize['zh-HK']?.subtitle || '',
            layout: Array.isArray(existingLocalize['zh-HK']?.layout) && existingLocalize['zh-HK'].layout.length > 0
              ? existingLocalize['zh-HK'].layout
              : JSON.parse(JSON.stringify(fallbackLayout)),
          },
        });
      })
      .catch((err) => {
        setLoading(false);
        console.error('Failed to load event details:', err);
        message.error(t('Failed to load event details') || '加载活动详情失败');
      });
  }, [id, t]);

  // Helper getters for current language's values
  const currentLocalized = localizeData[activeLang] || localizeData.cn;

  const handleLocalizedFieldChange = (field, value) => {
    setLocalizeData((prev) => ({
      ...prev,
      [activeLang]: {
        ...prev[activeLang],
        [field]: value,
      },
    }));
  };

  // Form Validation
  const validateForm = () => {
    setIsSubmitAttempted(true);
    if (!picUrl) {
      return false;
    }
    const mainTitle = localizeData.cn?.title || localizeData[activeLang]?.title;
    if (!mainTitle || !mainTitle.trim()) {
      return false;
    }
    if (!startDate) {
      return false;
    }
    if (!endDate) {
      return false;
    }
    if (dayjs(startDate).isAfter(dayjs(endDate), 'day')) {
      return false;
    }
    return true;
  };

  // Final Submit Handler (Single step)
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const mainTitle =
      localizeData.cn?.title ||
      localizeData.en?.title ||
      localizeData['zh-HK']?.title ||
      '';

    const imagesArray = picUrl
      ? [
          {
            url: picUrl,
            size: posterFileList[0]?.size || 100,
            name: posterFileList[0]?.name || 'poster.jpg',
          },
        ]
      : [];

    const localizePayload = {
      cn: {
        title: localizeData.cn?.title?.trim() || mainTitle,
        subtitle: localizeData.cn?.subtitle || '',
        subtitle1: '',
        layout: sanitizeLayout(localizeData.cn?.layout || DEFAULT_STANDARD_LAYOUT),
      },
    };

    if (localizeData.en?.title?.trim()) {
      localizePayload.en = {
        title: localizeData.en.title.trim(),
        subtitle: localizeData.en.subtitle || '',
        subtitle1: '',
        layout: sanitizeLayout(localizeData.en.layout || DEFAULT_STANDARD_LAYOUT),
      };
    }

    if (localizeData['zh-HK']?.title?.trim()) {
      localizePayload['zh-HK'] = {
        title: localizeData['zh-HK'].title.trim(),
        subtitle: localizeData['zh-HK'].subtitle || '',
        subtitle1: '',
        layout: sanitizeLayout(localizeData['zh-HK'].layout || DEFAULT_STANDARD_LAYOUT),
      };
    }

    const payload = {
      status: status, // "active" | "inactive"
      images: imagesArray,
      startDate: startDate && dayjs(startDate).isValid() ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
      endDate: endDate && dayjs(endDate).isValid() ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      localize: localizePayload,
    };

    const targetUpdateId = eventCode || id;
    const requestPromise = id
      ? putDataRequest(`${EVENTS_URL}/${targetUpdateId}`, payload)
      : postDataRequest(EVENTS_URL, payload);

    requestPromise
      .then(() => {
        setSubmitting(false);
        if (messageModalRef.current) {
          messageModalRef.current.showSuccessConfirmsAutoClose(
            t('Saved Successfully') || '保存成功',
            () => {},
            '',
            true
          );
        } else {
          message.success(t('Saved Successfully') || '保存成功');
        }

        setTimeout(() => {
          navigate('/events');
        }, 1200);
      })
      .catch((err) => {
        setSubmitting(false);
        console.error('Failed saving event:', err);
        message.error(err?.message || t('Save failed') || '保存失败');
      });
  };

  return (
    <>
      <HeaderCreateNewsComponent
        handleSubmit={handleSubmit}
        bntTitle={
          <div className="d-flex align-items-center">
            <Button
              onClick={handleSubmit}
              size="middle"
              style={{
                fontSize: '15px',
                backgroundColor: '#E50A47',
                border: '1px solid #e72158',
                color: '#FFFFFF',
                height: 36,
                borderRadius: 6,
                fontWeight: 500,
              }}
              loading={submitting}
              danger
            >
              {t('Submit') || '提交'}
            </Button>
          </div>
        }
        isHide="view"
        newStep={1}
        setps={[]}
        onClick={() => navigate('/events')}
        backTitle={id ? (t('Edit Event') || '编辑活动') : (t('Create an Event') || '创建活动')}
      >
        <Spin spinning={loading}>
          <div className="container newDetail">
            {/* Multilingual Selector */}
            {/* <div className="d-flex justify-content-end mb-4">
              <Radio.Group
                value={activeLang}
                onChange={(e) => setActiveLang(e.target.value)}
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="cn">简体中文</Radio.Button>
                <Radio.Button value="en">English</Radio.Button>
                <Radio.Button value="zh-HK">繁體中文</Radio.Button>
              </Radio.Group>
            </div> */}

            {/* Row 1: 封面 : */}
            <Row className="mb-5">
              <Col md={4} xs={24} className="userLabel" style={{ color: '#262626', fontSize: 14 }}>
                <span className="span-danger">* </span>
                {t('Cover') || '封面'} :
              </Col>
              <Col md={20} xs={24}>
                <Row className="w-100">
                  <Col md={24} sm={24}>
                    <div className={`w-100 ${isSubmitAttempted && !picUrl ? 'event-upload-cover-error' : ''}`}>
                      <OSSUploadWrapper
                        onUploadSuccess={(value) => {
                          if (value && value.url) {
                            setPicUrl(value.url);
                            setPosterFileList([value]);
                          }
                        }}
                      >
                        <DragUploadComponent
                          fileList={posterFileList}
                          setFileList={(values) => {
                            setPosterFileList(values);
                            if (!values || values.length === 0) {
                              setPicUrl('');
                            } else if (values[0]?.url) {
                              setPicUrl(values[0].url);
                            }
                          }}
                        />
                      </OSSUploadWrapper>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Row 2: 资讯标题 : (活动名称) */}
            <Row className="mt-4 d-flex align-items-center">
              <Col md={4} xs={24} className="userLabel" style={{ color: '#262626', fontSize: 14 }}>
                <span className="span-danger">* </span>
                {t('News Title') || t('Event Name') || '资讯标题'} :
              </Col>
              <Col md={20} xs={24}>
                <InputComponent
                  className="mb-2"
                  status={isSubmitAttempted && !currentLocalized.title ? 'error' : 'normal'}
                  value={currentLocalized.title}
                  setValue={(value) => handleLocalizedFieldChange('title', value)}
                  placeholder=""
                />
              </Col>
            </Row>

            {/* Row 3: 资讯副标题 : (活动副标题) */}
            <Row className="mt-4 d-flex align-items-center">
              <Col md={4} xs={24} className="userLabel" style={{ color: '#262626', fontSize: 14 }}>
                {t('News subtitle') || t('Event Subtitle') || '资讯副标题'} :
              </Col>
              <Col md={20} xs={24}>
                <InputComponent
                  className="mb-2"
                  value={currentLocalized.subtitle}
                  setValue={(value) => handleLocalizedFieldChange('subtitle', value)}
                  placeholder=""
                />
              </Col>
            </Row>

            {/* Row 4: 开始日期 */}
            <Row className="mt-4 d-flex align-items-center">
              <Col md={4} xs={24} className="userLabel" style={{ color: '#262626', fontSize: 14 }}>
                <span className="span-danger">* </span>
                {t('Start Date') || '开始日期'} :
              </Col>
              <Col md={20} xs={24}>
                <DateComponent
                  format="YYYY-MM-DD"
                  placeholder={t('Please select time or date range') || '请选择时间或时间段'}
                  value={startDate || null}
                  status={isSubmitAttempted && (!startDate || (endDate && dayjs(startDate).isAfter(dayjs(endDate), 'day'))) ? 'error' : 'normal'}
                  disabledDate={(current) => {
                    return endDate && current ? current.isAfter(dayjs(endDate), 'day') : false;
                  }}
                  setValue={(value) => {
                    const formatted = value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : (value || '');
                    setStartDate(formatted);
                    if (formatted && endDate && dayjs(formatted).isAfter(dayjs(endDate), 'day')) {
                      setEndDate('');
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>

            {/* Row 5: 结束日期 */}
            <Row className="mt-4 d-flex align-items-center">
              <Col md={4} xs={24} className="userLabel" style={{ color: '#262626', fontSize: 14 }}>
                <span className="span-danger">* </span>
                {t('End Date') || '结束日期'} :
              </Col>
              <Col md={20} xs={24}>
                <DateComponent
                  format="YYYY-MM-DD"
                  placeholder={t('Please select time or date range') || '请选择时间或时间段'}
                  value={endDate || null}
                  status={isSubmitAttempted && (!endDate || (startDate && dayjs(startDate).isAfter(dayjs(endDate), 'day'))) ? 'error' : 'normal'}
                  disabledDate={(current) => {
                    return startDate && current ? current.isBefore(dayjs(startDate), 'day') : false;
                  }}
                  setValue={(value) => {
                    const formatted = value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : (value || '');
                    setEndDate(formatted);
                  }}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>

            {/* Row 6: 状态 : (Status switch) */}
            {/* <Row className="mt-4 d-flex align-items-center pb-5">
              <Col md={4} xs={24} className="userLabel" style={{ color: '#262626', fontSize: 14 }}>
                {t('Status') || '状态'} :
              </Col>
              <Col md={20} xs={24}>
                <Switch
                  checked={status === 'active'}
                  onChange={(checked) => setStatus(checked ? 'active' : 'inactive')}
                />
              </Col>
            </Row> */}
          </div>
          <MessageConfirmModal textCentered ref={messageModalRef} />
        </Spin>
      </HeaderCreateNewsComponent>
    </>
  );
}

export default EventUpload;
